import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageSquareText, Flag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import ReportDialog from "../../components/feedback/ReportDialog";
import { Button } from "../../components/ui/button";
import { useApi } from "../../lib/useApi";
import { Loader, ErrorState, EmptyState } from "../../components/common/States";
import { Stars } from "../../components/common/Badges";
import { CATEGORY, fmtDate } from "../../lib/labels";

const Distribution = ({ dist }) => {
  const total = Object.values(dist).reduce((a, b) => a + b, 0);
  return (
    <div className="card-surface space-y-2 p-5" data-testid="reviews-distribution">
      {[5, 4, 3, 2, 1].map((n) => (
        <div key={n} className="flex items-center gap-3 text-sm">
          <span className="w-8 text-slate-600">{n} ★</span>
          <div className="h-2 flex-1 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-amber-400" style={{ width: total ? `${((dist[n] || 0) / total) * 100}%` : 0 }} /></div>
          <span className="w-6 text-right text-slate-500">{dist[n] || 0}</span>
        </div>
      ))}
    </div>
  );
};

const ReportLink = ({ id }) => {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (body) => {
    try { await api.post(`/public/reviews/${id}/report`, body); setDone(true); setOpen(false); toast.success("Merci, votre signalement sera examiné"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (done) return <span className="text-xs text-slate-400" data-testid={`review-reported-${id}`}>Signalé</span>;
  return (
    <>
      <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-rose-600" data-testid={`review-report-${id}`}><Flag className="h-3 w-3" />Signaler</button>
      {open && <ReportDialog testId="public-report" onSubmit={submit} onClose={() => setOpen(false)} />}
    </>
  );
};

const Review = ({ r }) => (
  <article className="card-surface p-5" data-testid={`public-review-${r.id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <Stars value={r.rating} /><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{CATEGORY[r.category] || "Avis"}</span>
      <span className="ml-auto text-xs text-slate-400">{fmtDate(r.created_at, false)}</span><ReportLink id={r.id} />
    </div>
    {r.title && <h3 className="mt-3 font-display font-semibold">{r.title}</h3>}
    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{r.message}</p>
    <p className="mt-3 text-xs text-slate-500">{r.public_author}{r.agency_name ? ` · ${r.agency_name}` : ""}{r.service_name ? ` · ${r.service_name}` : ""}{r.experience_date ? ` · expérience du ${fmtDate(r.experience_date, false)}` : ""}</p>
    {r.company_reply && (
      <div className="mt-4 rounded-lg border-l-2 border-blue-600 bg-blue-50 px-4 py-3 text-sm">
        <p className="text-xs font-semibold text-blue-800">Réponse de l'organisation · {fmtDate(r.company_reply.created_at, false)}</p>
        <p className="mt-1 whitespace-pre-wrap text-slate-700">{r.company_reply.message}</p>
      </div>
    )}
  </article>
);

export default function CompanyReviews() {
  const { slug } = useParams();
  const [f, setF] = useState({ rating: "", category: "", page: 1 });
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v !== "")).toString();
  const { data, loading, error, reload } = useApi(`/public/companies/${slug}/reviews?${qs}`);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, page: 1 });
  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <Link to={`/e/${slug}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900" data-testid="reviews-back-link"><ArrowLeft className="h-4 w-4" />Fiche de l'organisation</Link>
      <h1 className="mt-4 font-display text-3xl font-bold tracking-tight">Avis publics {data ? `— ${data.company.name}` : ""}</h1>
      <p className="mt-2 text-sm text-slate-500">Seuls les avis validés par l'organisation sont affichés. Les coordonnées des usagers ne sont jamais publiées.</p>
      {loading && !data && <Loader />}
      {error && <div className="mt-6"><ErrorState message={error} onRetry={reload} /></div>}
      {data && (
        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <div className="space-y-4">
            <Distribution dist={data.distribution} />
            <select className="field-select" value={f.rating} onChange={set("rating")} data-testid="reviews-rating-filter">
              <option value="">Toutes les notes</option>{[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoile(s)</option>)}
            </select>
            <select className="field-select" value={f.category} onChange={set("category")} data-testid="reviews-category-filter">
              <option value="">Toutes les catégories</option>{Object.entries(CATEGORY).filter(([k]) => k !== "reclamation").map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <Button asChild className="w-full rounded-full" data-testid="reviews-give-feedback"><Link to={`/e/${slug}#deposer`}>Déposer un avis</Link></Button>
          </div>
          <div className="space-y-3 lg:col-span-2">
            {!data.items.length && <EmptyState icon={MessageSquareText} title="Aucun avis publié" text="Les avis apparaissent ici après validation par l'organisation." />}
            {data.items.map((r) => <Review key={r.id} r={r} />)}
            {data.pages > 1 && (
              <div className="flex items-center justify-end gap-2 pt-2 text-sm text-slate-500">
                <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => setF({ ...f, page: f.page - 1 })} data-testid="reviews-prev">Précédent</Button>
                <span>{data.page} / {data.pages}</span>
                <Button variant="outline" size="sm" disabled={data.page >= data.pages} onClick={() => setF({ ...f, page: f.page + 1 })} data-testid="reviews-next">Suivant</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
